/**
 * Ítems oficiales NOM-035-STPS-2018.
 * Guía I: Acontecimientos Traumáticos Severos (ATS)
 * Guía II: Factores de Riesgo Psicosocial (16-50 empleados)
 * Guía III: Entorno Organizacional (>50 empleados)
 */

import type { TestItem } from '@/config/testItems';

export type Nom35ItemType = 'binary' | 'likert';

export interface Nom35Item {
  id: string;
  number: number;
  text: string;
  type: Nom35ItemType;
  section?: number;
  reverse?: boolean;
  category?: string;
  domain?: string;
}

export const NOM35_LIKERT_OPTIONS = [
  { value: 4, label: 'Siempre' },
  { value: 3, label: 'Casi siempre' },
  { value: 2, label: 'Algunas veces' },
  { value: 1, label: 'Casi nunca' },
  { value: 0, label: 'Nunca' },
];

export const NOM35_BINARY_OPTIONS = [
  { value: 0, label: 'No' },
  { value: 1, label: 'Sí' },
];

const createLikertItem = (idPrefix: string, number: number, text: string, isReverse: boolean): Nom35Item => ({
  id: `${idPrefix}_${number}`,
  number,
  type: 'likert',
  text,
  reverse: isReverse,
});

export const NOM35_G1_ITEMS: Nom35Item[] = [
  { id: 'g1_1', number: 1, section: 1, type: 'binary', text: '¿Ha presenciado o sufrido alguna vez, durante o con motivo del trabajo, un accidente que tenga como consecuencia la muerte, la pérdida de un miembro o una lesión grave?' },
  { id: 'g1_2', number: 2, section: 1, type: 'binary', text: '¿Ha presenciado o sufrido asaltos?' },
  { id: 'g1_3', number: 3, section: 1, type: 'binary', text: '¿Ha presenciado o sufrido actos violentos que derivaron en lesiones graves?' },
  { id: 'g1_4', number: 4, section: 1, type: 'binary', text: '¿Ha presenciado o sufrido secuestro?' },
  { id: 'g1_5', number: 5, section: 1, type: 'binary', text: '¿Ha presenciado o sufrido amenazas?' },
  { id: 'g1_6', number: 6, section: 1, type: 'binary', text: '¿Ha presenciado o sufrido cualquier otro suceso que ponga en riesgo su vida o salud, y/o la de otras personas?' },
  { id: 'g1_7', number: 7, section: 2, type: 'binary', text: '¿Ha tenido recuerdos recurrentes sobre el acontecimiento que le provocan malestares?' },
  { id: 'g1_8', number: 8, section: 2, type: 'binary', text: '¿Ha tenido sueños de carácter recurrente sobre el acontecimiento, que le producen malestar?' },
  { id: 'g1_9', number: 9, section: 3, type: 'binary', text: '¿Se ha esforzado por evitar todo tipo de sentimientos, conversaciones o situaciones que le puedan recordar el acontecimiento?' },
  { id: 'g1_10', number: 10, section: 3, type: 'binary', text: '¿Se ha esforzado por evitar todo tipo de actividades, lugares o personas que motivan recuerdos del acontecimiento?' },
  { id: 'g1_11', number: 11, section: 3, type: 'binary', text: '¿Ha tenido dificultad para recordar alguna parte importante del evento?' },
  { id: 'g1_12', number: 12, section: 3, type: 'binary', text: '¿Ha disminuido su interés en sus actividades cotidianas?' },
  { id: 'g1_13', number: 13, section: 3, type: 'binary', text: '¿Se ha sentido usted alejado o distante de los demás?' },
  { id: 'g1_14', number: 14, section: 3, type: 'binary', text: '¿Ha notado que tiene dificultad para expresar sus sentimientos?' },
  { id: 'g1_15', number: 15, section: 3, type: 'binary', text: '¿Ha tenido la impresión de que su vida se va a acortar, que va a morir antes que otras personas o que tiene un futuro limitado?' },
  { id: 'g1_16', number: 16, section: 4, type: 'binary', text: '¿Ha tenido usted dificultades para dormir?' },
  { id: 'g1_17', number: 17, section: 4, type: 'binary', text: '¿Ha estado particularmente irritable o le han dado arranques de coraje?' },
  { id: 'g1_18', number: 18, section: 4, type: 'binary', text: '¿Ha tenido dificultad para concentrarse?' },
  { id: 'g1_19', number: 19, section: 4, type: 'binary', text: '¿Ha estado nervioso o constantemente en alerta?' },
  { id: 'g1_20', number: 20, section: 4, type: 'binary', text: '¿Se ha sobresaltado fácilmente por cualquier cosa?' },
];

export const NOM35_G2_ITEMS: Nom35Item[] = [
  createLikertItem('g2', 1, 'Mi trabajo me exige hacer mucho esfuerzo físico', true),
  createLikertItem('g2', 2, 'Me preocupa sufrir un accidente en mi trabajo', true),
  createLikertItem('g2', 3, 'Considero que las actividades que realizo son peligrosas', true),
  createLikertItem('g2', 4, 'Por la cantidad de trabajo que tengo debo quedarme tiempo adicional a mi turno', true),
  createLikertItem('g2', 5, 'Por la cantidad de trabajo que tengo debo trabajar sin parar', true),
  createLikertItem('g2', 6, 'Considero que es necesario mantener un ritmo de trabajo acelerado', true),
  createLikertItem('g2', 7, 'Mi trabajo exige que esté muy concentrado', true),
  createLikertItem('g2', 8, 'Mi trabajo requiere que memorice mucha información', true),
  createLikertItem('g2', 9, 'Mi trabajo exige que atienda varios asuntos al mismo tiempo', true),
  createLikertItem('g2', 10, 'En mi trabajo soy responsable de cosas de mucho valor', true),
  createLikertItem('g2', 11, 'Respondo ante mi jefe por los resultados de toda mi área de trabajo', true),
  createLikertItem('g2', 12, 'En mi trabajo me dan órdenes contradictorias', true),
  createLikertItem('g2', 13, 'Considero que en mi trabajo me piden hacer cosas innecesarias', true),
  createLikertItem('g2', 14, 'Trabajo horas extras más de tres veces a la semana', true),
  createLikertItem('g2', 15, 'Mi trabajo me exige laborar en días de descanso, festivos o fines de semana', true),
  createLikertItem('g2', 16, 'Considero que el tiempo en el trabajo es mucho y perjudica mis actividades familiares o personales', true),
  createLikertItem('g2', 17, 'Pienso en las actividades familiares o personales cuando estoy en mi trabajo', true),
  createLikertItem('g2', 18, 'Mi trabajo permite que desarrolle nuevas habilidades', false),
  createLikertItem('g2', 19, 'En mi trabajo puedo aspirar a un mejor puesto', false),
  createLikertItem('g2', 20, 'Durante mi jornada de trabajo puedo tomar pausas cuando las necesito', false),
  createLikertItem('g2', 21, 'Puedo decidir la velocidad a la que realizo mis actividades en mi trabajo', false),
  createLikertItem('g2', 22, 'Puedo cambiar el orden de las actividades que realizo en mi trabajo', false),
  createLikertItem('g2', 23, 'Me informan con claridad cuáles son mis funciones', false),
  createLikertItem('g2', 24, 'Me explican claramente los resultados que debo obtener en mi trabajo', false),
  createLikertItem('g2', 25, 'Me informan con quién puedo resolver problemas o asuntos de trabajo', false),
  createLikertItem('g2', 26, 'Me permiten asistir a capacitaciones relacionadas con mi trabajo', false),
  createLikertItem('g2', 27, 'Recibo capacitación útil para hacer mi trabajo', false),
  createLikertItem('g2', 28, 'Mi jefe tiene en cuenta mis puntos de vista y opiniones', false),
  createLikertItem('g2', 29, 'Mi jefe ayuda a solucionar los problemas que se presentan en el trabajo', false),
  createLikertItem('g2', 30, 'Puedo confiar en mis compañeros de trabajo', false),
  createLikertItem('g2', 31, 'Cuando tenemos que realizar trabajo de equipo los compañeros colaboran', false),
  createLikertItem('g2', 32, 'Mis compañeros de trabajo me ayudan cuando tengo dificultades', false),
  createLikertItem('g2', 33, 'En mi trabajo puedo expresarme libremente sin interrupciones', false),
  createLikertItem('g2', 34, 'Recibo críticas constantes a mi persona y/o trabajo', true),
  createLikertItem('g2', 35, 'Recibo burlas, calumnias, difamaciones, humillaciones o ridiculizaciones', true),
  createLikertItem('g2', 36, 'Se ignora mi presencia o se me excluye de las reuniones de trabajo y en la toma de decisiones', true),
  createLikertItem('g2', 37, 'Se manipulan las situaciones de trabajo para hacerme parecer un mal trabajador', true),
  createLikertItem('g2', 38, 'Se ignoran mis éxitos laborales y se atribuyen a otros trabajadores', true),
  createLikertItem('g2', 39, 'Me bloquean o impiden las oportunidades que tengo para obtener ascenso o mejora en mi trabajo', true),
  createLikertItem('g2', 40, 'He presenciado actos de violencia en mi centro de trabajo', true),
  createLikertItem('g2', 41, 'Atiendo clientes o usuarios muy enojados', true),
  createLikertItem('g2', 42, 'Mi trabajo me exige atender personas muy necesitadas de ayuda o enfermas', true),
  createLikertItem('g2', 43, 'Para hacer mi trabajo debo demostrar sentimientos distintos a los míos', true),
  createLikertItem('g2', 44, 'Comunican tarde los asuntos de trabajo', true),
  createLikertItem('g2', 45, 'Dificultan el logro de los resultados del trabajo', true),
  createLikertItem('g2', 46, 'Ignoran las sugerencias para mejorar su trabajo', true),
];

export const NOM35_G3_ITEMS: Nom35Item[] = [
  createLikertItem('g3', 1, 'El espacio donde trabajo me permite realizar mis actividades de manera segura e higiénica', false),
  createLikertItem('g3', 2, 'Mi trabajo me exige hacer mucho esfuerzo físico', true),
  createLikertItem('g3', 3, 'Me preocupa sufrir un accidente en mi trabajo', true),
  createLikertItem('g3', 4, 'Considero que en mi trabajo se aplican las normas de seguridad y salud en el trabajo', false),
  createLikertItem('g3', 5, 'Considero que las actividades que realizo son peligrosas', true),
  createLikertItem('g3', 6, 'Por la cantidad de trabajo que tengo debo quedarme tiempo adicional a mi turno', true),
  createLikertItem('g3', 7, 'Por la cantidad de trabajo que tengo debo trabajar sin parar', true),
  createLikertItem('g3', 8, 'Considero que es necesario mantener un ritmo de trabajo acelerado', true),
  createLikertItem('g3', 9, 'Mi trabajo exige que esté muy concentrado', true),
  createLikertItem('g3', 10, 'Mi trabajo requiere que memorice mucha información', true),
  createLikertItem('g3', 11, 'En mi trabajo tengo que tomar decisiones difíciles muy rápido', true),
  createLikertItem('g3', 12, 'Mi trabajo exige que atienda varios asuntos al mismo tiempo', true),
  createLikertItem('g3', 13, 'En mi trabajo soy responsable de cosas de mucho valor', true),
  createLikertItem('g3', 14, 'Respondo ante mi jefe por los resultados de toda mi área de trabajo', true),
  createLikertItem('g3', 15, 'En el trabajo me dan órdenes contradictorias', true),
  createLikertItem('g3', 16, 'Considero que en mi trabajo me piden hacer cosas innecesarias', true),
  createLikertItem('g3', 17, 'Trabajo horas extras más de tres veces a la semana', true),
  createLikertItem('g3', 18, 'Mi trabajo me exige laborar en días de descanso, festivos o fines de semana', true),
  createLikertItem('g3', 19, 'Considero que el tiempo en el trabajo es mucho y perjudica mis actividades familiares o personales', true),
  createLikertItem('g3', 20, 'Debo atender asuntos de trabajo cuando estoy en casa', true),
  createLikertItem('g3', 21, 'Pienso en las actividades familiares o personales cuando estoy en mi trabajo', true),
  createLikertItem('g3', 22, 'Pienso que mis responsabilidades familiares afectan mi trabajo', true),
  createLikertItem('g3', 23, 'Mi trabajo permite que desarrolle nuevas habilidades', false),
  createLikertItem('g3', 24, 'En mi trabajo puedo aspirar a un mejor puesto', false),
  createLikertItem('g3', 25, 'Durante mi jornada de trabajo puedo tomar pausas cuando las necesito', false),
  createLikertItem('g3', 26, 'Puedo decidir cuánto trabajo realizo durante la jornada laboral', false),
  createLikertItem('g3', 27, 'Puedo decidir la velocidad a la que realizo mis actividades en mi trabajo', false),
  createLikertItem('g3', 28, 'Puedo cambiar el orden de las actividades que realizo en mi trabajo', false),
  createLikertItem('g3', 29, 'Los cambios que se presentan en mi trabajo dificultan mi labor', true),
  createLikertItem('g3', 30, 'Cuando se presentan cambios en mi trabajo se tienen en cuenta mis ideas o aportaciones', false),
  createLikertItem('g3', 31, 'Me informan con claridad cuáles son mis funciones', false),
  createLikertItem('g3', 32, 'Me explican claramente los resultados que debo obtener en mi trabajo', false),
  createLikertItem('g3', 33, 'Me explican claramente los objetivos de mi trabajo', false),
  createLikertItem('g3', 34, 'Me informan con quién puedo resolver problemas o asuntos de trabajo', false),
  createLikertItem('g3', 35, 'Me permiten asistir a capacitaciones relacionadas con mi trabajo', false),
  createLikertItem('g3', 36, 'Recibo capacitación útil para hacer mi trabajo', false),
  createLikertItem('g3', 37, 'Mi jefe ayuda a organizar mejor el trabajo', false),
  createLikertItem('g3', 38, 'Mi jefe tiene en cuenta mis puntos de vista y opiniones', false),
  createLikertItem('g3', 39, 'Mi jefe me comunica a tiempo la información relacionada con el trabajo', false),
  createLikertItem('g3', 40, 'La orientación que me da mi jefe me ayuda a realizar mejor mi trabajo', false),
  createLikertItem('g3', 41, 'Mi jefe ayuda a solucionar los problemas que se presentan en el trabajo', false),
  createLikertItem('g3', 42, 'Puedo confiar en mis compañeros de trabajo', false),
  createLikertItem('g3', 43, 'Entre compañeros solucionamos los problemas de trabajo de forma respetuosa', false),
  createLikertItem('g3', 44, 'En mi trabajo me hacen sentir parte del grupo', false),
  createLikertItem('g3', 45, 'Cuando tenemos que realizar trabajo de equipo los compañeros colaboran', false),
  createLikertItem('g3', 46, 'Mis compañeros de trabajo me ayudan cuando tengo dificultades', false),
  createLikertItem('g3', 47, 'Me informan sobre lo que hago bien en mi trabajo', false),
  createLikertItem('g3', 48, 'La forma como evalúan mi trabajo en mi centro de trabajo me ayuda a mejorar mi desempeño', false),
  createLikertItem('g3', 49, 'En mi centro de trabajo me pagan a tiempo mi salario', false),
  createLikertItem('g3', 50, 'El pago que recibo es el que merezco por el trabajo que realizo', false),
  createLikertItem('g3', 51, 'Si obtengo los resultados esperados en mi trabajo me recompensan o reconocen', false),
  createLikertItem('g3', 52, 'Las personas que hacen bien el trabajo pueden crecer laboralmente', false),
  createLikertItem('g3', 53, 'Considero que mi trabajo es estable', false),
  createLikertItem('g3', 54, 'En mi trabajo existe continua rotación de personal', true),
  createLikertItem('g3', 55, 'Siento orgullo de laborar en este centro de trabajo', false),
  createLikertItem('g3', 56, 'Me siento comprometido con mi trabajo', false),
  createLikertItem('g3', 57, 'En mi trabajo puedo expresarme libremente sin interrupciones', false),
  createLikertItem('g3', 58, 'Recibo críticas constantes a mi persona y/o trabajo', true),
  createLikertItem('g3', 59, 'Recibo burlas, calumnias, difamaciones, humillaciones o ridiculizaciones', true),
  createLikertItem('g3', 60, 'Se ignora mi presencia o se me excluye de las reuniones de trabajo y en la toma de decisiones', true),
  createLikertItem('g3', 61, 'Se manipulan las situaciones de trabajo para hacerme parecer un mal trabajador', true),
  createLikertItem('g3', 62, 'Se ignoran mis éxitos laborales y se atribuyen a otros trabajadores', true),
  createLikertItem('g3', 63, 'Me bloquean o impiden las oportunidades que tengo para obtener ascenso o mejora en mi trabajo', true),
  createLikertItem('g3', 64, 'He presenciado actos de violencia en mi centro de trabajo', true),
  createLikertItem('g3', 65, 'Atiendo clientes o usuarios muy enojados', true),
  createLikertItem('g3', 66, 'Mi trabajo me exige atender personas muy necesitadas de ayuda o enfermas', true),
  createLikertItem('g3', 67, 'Para hacer mi trabajo debo demostrar sentimientos distintos a los míos', true),
  createLikertItem('g3', 68, 'Mi trabajo me exige atender situaciones de violencia', true),
  createLikertItem('g3', 69, 'Comunican tarde los asuntos de trabajo', true),
  createLikertItem('g3', 70, 'Dificultan el logro de los resultados del trabajo', true),
  createLikertItem('g3', 71, 'Cooperan poco cuando se necesita', true),
  createLikertItem('g3', 72, 'Ignoran las sugerencias para mejorar su trabajo', true),
];

function toTestItem(item: Nom35Item): TestItem {
  if (item.type === 'binary') {
    return {
      id: item.id,
      text: item.text,
      type: 'binary',
      options: [...NOM35_BINARY_OPTIONS],
    };
  }
  return {
    id: item.id,
    text: item.text,
    type: 'likert',
    options: [...NOM35_LIKERT_OPTIONS],
    reverse: item.reverse,
  };
}

export const NOM35_G1_ITEMS_FOR_ENGINE: TestItem[] = NOM35_G1_ITEMS.map(toTestItem);
export const NOM35_G2_ITEMS_FOR_ENGINE: TestItem[] = NOM35_G2_ITEMS.map(toTestItem);
export const NOM35_G3_ITEMS_FOR_ENGINE: TestItem[] = NOM35_G3_ITEMS.map(toTestItem);
